import { getIncenseApiBaseUrl } from '../utils/getIncenseApiBaseUrl'
import { IncenseEncryptionService, type IncenseEncryptionConfig } from './IncenseEncryptionService'

/**
 * Factory for creating and managing IncenseEncryptionService instances
 *
 * Provides singleton-like behavior with configuration management for easy
 * integration across multiple adapters and components.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class IncenseEncryptionFactory {
  private static instance: IncenseEncryptionService | null = null
  private static defaultConfig: IncenseEncryptionConfig | null = null

  /**
   * Creates or returns the singleton instance of IncenseEncryptionService
   *
   * @param config - Configuration for the encryption service
   * @returns The encryption service instance
   */
  static create(config: IncenseEncryptionConfig): IncenseEncryptionService {
    if (!this.instance || this.hasConfigChanged(config)) {
      this.instance = new IncenseEncryptionService(config)
      this.defaultConfig = config
    }
    return this.instance
  }

  /**
   * Gets the current instance if it exists
   *
   * @returns The current encryption service instance or null if not created
   */
  static getInstance(): IncenseEncryptionService | null {
    return this.instance
  }

  /**
   * Creates a new instance with custom configuration (not singleton)
   *
   * @param config - Custom configuration for this specific instance
   * @returns A new encryption service instance
   */
  static createCustom(config: IncenseEncryptionConfig): IncenseEncryptionService {
    return new IncenseEncryptionService(config)
  }

  /**
   * Resets the singleton instance (useful for testing)
   */
  static reset(): void {
    this.instance = null
    this.defaultConfig = null
  }

  /**
   * Checks if the provided config differs from the current default config
   */
  private static hasConfigChanged(config: IncenseEncryptionConfig): boolean {
    if (!this.defaultConfig) return true

    return (
      this.defaultConfig.baseUrl !== config.baseUrl ||
      this.defaultConfig.timeout !== config.timeout ||
      JSON.stringify(this.defaultConfig.retry) !== JSON.stringify(config.retry)
    )
  }
}

/**
 * Default configuration resolved from environment with staging/prod fallbacks
 */
export const DEFAULT_ENCRYPTION_CONFIG: IncenseEncryptionConfig = {
  baseUrl: getIncenseApiBaseUrl(),
  timeout: 5000,
  retry: {
    attempts: 3,
    delay: 1000,
  },
}
